/**
 * Manage or Request Access — client-side entity executor (ADR-034).
 *
 * Runs in the HOST page, never in the plugin iframe. A plugin opens the
 * host's access sheet for the workspace the person is acting in; the host
 * draws it from the server's own answers (platform content, the "who draws
 * the pixels" rule) and every change is the host session's, through the
 * Access routes. The plugin supplies nothing and learns only whether the
 * person's own access may have changed.
 *
 * Host services arrive through `configureAppAccessDialogHost` at registration
 * (`client/src/lib/register-client-entities.ts`), because the executor context
 * carries only `{ userId, archeId, holderId }`.
 *
 * Expected failures are RESULTS, not throws, so a typed code reaches the SDK.
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const APP_ACCESS_DIALOG_ENTITY_ID = 'ent_intg_app_access_dialog_00000';

export type AppAccessDialogErrorCode =
  | 'INVALID_INPUT'
  | 'ACCESS_UNAVAILABLE'
  | 'NOT_IN_WORKSPACE'
  | 'SHEET_BUSY';

export type AppAccessDialogResult =
  | { changed: boolean }
  | { error: { code: AppAccessDialogErrorCode; message: string } };

/** What the host page provides: the sheet, drawn for one arche in one workspace. */
export interface AppAccessDialogHost {
  showAccessSheet(request: { archeId: string; holderId: string }): Promise<AppAccessDialogResult>;
}

let host: AppAccessDialogHost | null = null;

/** Called once by the host page at registration. */
export function configureAppAccessDialogHost(next: AppAccessDialogHost | null): void {
  host = next;
}

export async function appAccessDialogExecutor(
  input: unknown,
  context: ClientEntityInvocationContext,
): Promise<AppAccessDialogResult> {
  // The sheet takes nothing from the plugin: which workspace and which person
  // are the host's to say.
  if (
    input !== undefined &&
    input !== null &&
    !(typeof input === 'object' && !Array.isArray(input) && Object.keys(input).length === 0)
  ) {
    return { error: { code: 'INVALID_INPUT', message: 'The access sheet takes no input' } };
  }
  if (!context.holderId) {
    return {
      error: {
        code: 'NOT_IN_WORKSPACE',
        message: 'App roles apply in a shared workspace; the person is in their Personal one',
      },
    };
  }
  if (!host) {
    return {
      error: { code: 'ACCESS_UNAVAILABLE', message: 'Managing access is not available here' },
    };
  }
  return host.showAccessSheet({ archeId: context.archeId, holderId: context.holderId });
}

export const appAccessDialogDefinition = {
  entityId: APP_ACCESS_DIALOG_ENTITY_ID,
  rateLimitPerMinute: 10,
  pluginPermission: 'data:workspace',
  executor: appAccessDialogExecutor,
} as const satisfies ClientEntityDefinition<unknown, AppAccessDialogResult>;
